/**
 * Non-QM tab.
 *
 * Doc Type drives three Vesta fields:
 *   - loanProduct.alternativeIncomeVerificationType (this dropdown's value, 1:1)
 *   - loanProduct.alternativeIncomeDocumentationNumberOfMonths (only for bank statements)
 *   - loan.underwrittenUsingDSCRIndicator (true iff Doc Type = DSCR, else false)
 *
 * Plus an editable DSCR Ratio (Estimate) → loan.subjectPropertyDSCREstimate when DSCR is selected.
 *
 * The tab is only mounted when nonQualifiedMortgageIndicator is true (driven by the Loan tab's
 * Mortgage Type dropdown), so we don't repeat the Non-QM toggle here.
 */
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Card, CardContent, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import DatasetIcon from '@mui/icons-material/Dataset';
import CalculateIcon from '@mui/icons-material/Calculate';
import EditableField from '../../../components/EditableField';
import SectionHeader from '../../../components/shared/SectionHeader';
import DscrCalculatorModal from './DscrCalculatorModal';
import { getDisplayTotalMonthlyPayment } from '../../../utils/monthlyPaymentDisplay';
import {
  BANK_STATEMENT_DOC_TYPES,
  BANK_STATEMENT_MONTHS_OPTIONS,
  DOC_TYPE_DSCR,
  DOC_TYPE_OPTIONS,
} from '../../../utils/nonQm';

const pick = (nonQm, camel, pascal) => nonQm?.[camel] ?? nonQm?.[pascal] ?? null;

const NonQmTab = ({ nonQm, loan, isEditMode = false }) => {
  const { setValue } = useFormContext();
  const docType = useWatch({ name: 'nonQm.alternativeIncomeVerificationType' });
  const watchedDscrEstimate = useWatch({ name: 'nonQm.subjectPropertyDSCREstimate' });
  const n = nonQm || {};
  const docTypeValue = pick(n, 'alternativeIncomeVerificationType', 'AlternativeIncomeVerificationType');
  const monthsValue = pick(n, 'alternativeIncomeDocumentationNumberOfMonths', 'AlternativeIncomeDocumentationNumberOfMonths');
  const dscrEstimateValue = pick(n, 'subjectPropertyDSCREstimate', 'SubjectPropertyDSCREstimate');
  const effectiveDocType = docType || docTypeValue;
  const isBankStatements = BANK_STATEMENT_DOC_TYPES.has(effectiveDocType);
  const isDscr = effectiveDocType === DOC_TYPE_DSCR;
  const displayDscrEstimate = watchedDscrEstimate ?? dscrEstimateValue;
  const [calculatorOpen, setCalculatorOpen] = useState(false);

  /**
   * Keep underwrittenUsingDSCRIndicator in lockstep with the Doc Type.
   * Also clear inapplicable secondary inputs so they don't leak into the patch
   * if the user switches doc types mid-edit.
   */
  useEffect(() => {
    if (!isEditMode) return;
    if (docType == null || docType === '') return;
    setValue('nonQm.underwrittenUsingDSCRIndicator', isDscr, { shouldDirty: true });
    if (!isBankStatements) {
      setValue('nonQm.alternativeIncomeDocumentationNumberOfMonths', null, { shouldDirty: true });
    }
    if (docType != null && docType !== '' && !isDscr) {
      setValue('nonQm.subjectPropertyDSCREstimate', null, { shouldDirty: true });
    }
  }, [docType, isBankStatements, isDscr, isEditMode, setValue]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Non-QM details. Pick the documentation type so the LOS captures the right underwriting
        method; these values flow to pricing so the correct Non-QM products are returned.
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <SectionHeader title="Documentation" icon={<DatasetIcon fontSize="small" />} />
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <EditableField
                label="Doc Type"
                value={docTypeValue}
                name="nonQm.alternativeIncomeVerificationType"
                isEditMode={isEditMode}
                options={DOC_TYPE_OPTIONS}
                required={isEditMode}
                highlight={isEditMode && (docTypeValue == null || docTypeValue === '')}
              />
            </Grid>

            {isBankStatements && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <EditableField
                  label="Bank Statement Months"
                  value={monthsValue}
                  name="nonQm.alternativeIncomeDocumentationNumberOfMonths"
                  isEditMode={isEditMode}
                  options={BANK_STATEMENT_MONTHS_OPTIONS}
                  required={isEditMode}
                  highlight={isEditMode && (monthsValue == null || monthsValue === '')}
                />
              </Grid>
            )}

            {isDscr && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <EditableField
                      label="DSCR Ratio (Estimate)"
                      value={displayDscrEstimate}
                      name="nonQm.subjectPropertyDSCREstimate"
                      isEditMode={isEditMode}
                      type="number"
                      placeholder="e.g. 1.250"
                    />
                  </Box>
                  {isEditMode && (
                    <Tooltip title="Calculate DSCR ratio">
                      <IconButton
                        size="small"
                        onClick={() => setCalculatorOpen(true)}
                        sx={{ mt: 0.5, border: 1, borderColor: 'divider' }}
                        aria-label="Open DSCR calculator"
                      >
                        <CalculateIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <DscrCalculatorModal
        open={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
        currentEstimate={displayDscrEstimate}
        defaultHousingExpenses={getDisplayTotalMonthlyPayment(loan)}
        onSave={(value) => setValue('nonQm.subjectPropertyDSCREstimate', value, { shouldDirty: true })}
      />
    </Box>
  );
};

export default NonQmTab;
